import { zodResolver } from '@hookform/resolvers/zod';
import { Grid, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  EditableLegalClientSchema,
  IEditableLegalClient,
} from '../../../../../../Interfaces/IEditLegalClient';
import {
  getEmptyLegalClient,
  ILegalClient,
} from '../../../../../../Interfaces/ILegalClient';
import { ILegalTransactionRelationship } from '../../../../../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationship';
import {
  getEmptyMarriageRelationshipData,
  IMarriageRelationshipData,
  MarriageRelationshipDataSchema,
} from '../../../../../../Interfaces/ILegalTransactionRelationship/Relationships/Marriage/ILegalTransactionRelationshipMarriageData';
import { getEmptyNaturalPerson } from '../../../../../../Interfaces/INaturalPerson';
import { DialogResponsive } from '../../../../../core/DialogResponsive';
import { EditLegalClient } from '../../../../../LegalClient/EditLegalClient';
import { useLegalClientStore } from '../../../generic/legalClientStore';
import { NavRow } from '../../../generic/NavRow';
import { isMarriageRelationship } from '../../../Inherit/utilities';
import { DivorceDateField } from './fields/DivorceDateField';
import { LegalClientNodeContext } from './NewFamilyTree';

type EditLegalClientDialogProps = {
  idLegalClient: number;
  relationshipArray: ILegalTransactionRelationship[];
  legalClientNodeContext: LegalClientNodeContext[];
  isOpen: boolean;
  onClose: () => void;
};

export const EditLegalClientDialog: React.FC<EditLegalClientDialogProps> = ({
  idLegalClient,
  relationshipArray,
  legalClientNodeContext,
  isOpen,
  onClose,
}) => {
  const [putLegalClient, getLegalClient] = useLegalClientStore(state => [
    state.putLegalClient,
    state.getLegalClient,
  ]);

  const legalClientMethods = useForm<IEditableLegalClient>({
    mode: 'onChange',
    defaultValues: {
      ...getEmptyLegalClient(),
      ...getEmptyNaturalPerson(),
      idLegalClient: 1,
    },
    resolver: zodResolver(EditableLegalClientSchema),
  });

  const marriageMemberDataMethods = useForm<IMarriageRelationshipData>({
    mode: 'onChange',
    defaultValues: getEmptyMarriageRelationshipData(),
    resolver: zodResolver(MarriageRelationshipDataSchema),
  });

  useEffect(() => {
    if (isOpen) {
      const legalClient = getLegalClient(idLegalClient);

      if (!legalClient) {
        throw new Error(
          `LegalClient with id ${idLegalClient} not found in legalClientStore`
        );
      }

      legalClientMethods.reset({ ...legalClient } as IEditableLegalClient);

      const marriageRelationship = relationshipArray
        .filter(isMarriageRelationship)
        .find(rel => rel.Members.some(m => m.idLegalClient === idLegalClient));

      if (!marriageRelationship) {
        return;
      }

      const member = marriageRelationship.Members.find(
        m => m.idLegalClient === idLegalClient
      );

      marriageMemberDataMethods.reset(
        member?.data || getEmptyMarriageRelationshipData()
      );
    }
  }, [isOpen, idLegalClient, getLegalClient, legalClientMethods]);

  async function onSubmit() {
    const isValid = await legalClientMethods.trigger();

    if (!isValid) {
      return;
    }

    const data = legalClientMethods.getValues();
    putLegalClient(data as ILegalClient);
    onClose();
  }

  return (
    <DialogResponsive
      open={isOpen}
      onClose={onClose}
      title='Person bearbeiten'
      maxWidth='lg'
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormProvider {...legalClientMethods}>
            <form id='testator-legal-client-form' noValidate>
              <EditLegalClient disableCompanyLegalClient />
            </form>
          </FormProvider>
        </Grid>

        {[LegalClientNodeContext.Spouse, LegalClientNodeContext.ExSpouse].some(
          context => legalClientNodeContext.includes(context)
        ) && (
          <Grid item xs={12}>
            <FormProvider {...marriageMemberDataMethods}>
              <form id='testator-member-data-form' noValidate>
                <Typography variant='body2' mb={1}>
                  Wenn die Ehe geschieden wurde, geben Sie bitte das
                  Scheidungsdatum an. Wenn die Ehe noch besteht, lassen Sie das
                  Feld leer.
                </Typography>
                <DivorceDateField<IMarriageRelationshipData> name='divorceDate' />
              </form>
            </FormProvider>
          </Grid>
        )}

        <Grid item xs={12}>
          <NavRow
            nextButtonText='Speichern'
            handleNext={onSubmit}
            handleBack={onClose}
          />
        </Grid>
      </Grid>
    </DialogResponsive>
  );
};
